import { BookmarkAccess, ScopedCatalogEntry } from "types/bookmark";

import { type Filters } from "./types";

let pendingCapture: ((canvas: HTMLCanvasElement) => void) | null = null;

export function notifyFrameRendered(webglCanvas: HTMLCanvasElement): void {
    if (!pendingCapture) {
        return;
    }
    const cb = pendingCapture;
    pendingCapture = null;
    // Copy pixels synchronously before the framebuffer is cleared on the next draw
    const copy = document.createElement("canvas");
    copy.width = webglCanvas.width;
    copy.height = webglCanvas.height;
    copy.getContext("2d")!.drawImage(webglCanvas, 0, 0);
    cb(copy);
}

function captureNextFrame(): Promise<HTMLCanvasElement> {
    if (pendingCapture) {
        return Promise.reject(new Error("Snapshot capture already in progress"));
    }
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            pendingCapture = null;
            reject(new Error("Snapshot frame capture timed out"));
        }, 5000);
        pendingCapture = (canvas) => {
            clearTimeout(timer);
            resolve(canvas);
        };
    });
}

// Whether a collection path is inside `collection` — the collection itself or one of its descendants.
// Matches on a path-segment boundary so a sibling sharing a string prefix ("floor 1" vs "floor 10") is
// not included.
function isInCollection(path: string | undefined, collection: string): boolean {
    return path === collection || (path?.startsWith(collection + "/") ?? false);
}

// Rewrites one collection path for a rename of collection `from` to `to`. An empty `to` moves it to the
// top level.
export function renameCollectionPath(path: string | undefined, from: string, to: string): string | undefined {
    if (path === undefined || !isInCollection(path, from)) {
        return path;
    }

    const suffix = path.slice(from.length);
    return to === "" ? suffix.replace(/^\//, "") : to + suffix;
}

/**
 * The scopes holding at least one bookmark in `collection` (or in one of its descendants), so an edit to
 * the collection can skip the scope it does not touch. Pass the full catalog: deciding from a filtered one
 * would leave the bookmarks the filter hides behind in the old collection.
 */
export function collectionScopes(catalog: ScopedCatalogEntry[], collection: string): BookmarkAccess[] {
    return [BookmarkAccess.Public, BookmarkAccess.Personal].filter((access) =>
        catalog.some((entry) => entry.access === access && isInCollection(entry.grouping, collection)),
    );
}

/**
 * The grouping paths to offer when picking one: every path the catalog uses, plus each of their
 * ancestors, so an existing grouping can be nested under without typing the parent out.
 */
export function groupingOptions(catalog: ScopedCatalogEntry[], locale: string): string[] {
    const paths = new Set<string>();

    for (const entry of catalog) {
        if (!entry.grouping) {
            continue;
        }

        const segments = entry.grouping.split("/");
        for (let depth = 1; depth <= segments.length; depth++) {
            paths.add(segments.slice(0, depth).join("/"));
        }
    }

    paths.delete("");

    return [...paths].sort((a, b) => a.localeCompare(b, locale, { sensitivity: "accent" }));
}

export function filterCatalog(catalog: ScopedCatalogEntry[], filters: Filters, locale: string): ScopedCatalogEntry[] {
    const text = filters.text.toLowerCase();

    const matches = catalog.filter((entry) => {
        if (
            text &&
            !entry.name.toLowerCase().includes(text) &&
            !entry.description?.toLowerCase().includes(text) &&
            !entry.grouping?.toLowerCase().includes(text)
        ) {
            return false;
        }

        if (filters.personal && entry.access !== BookmarkAccess.Personal) {
            return false;
        }

        if (filters.public && entry.access !== BookmarkAccess.Public) {
            return false;
        }

        return true;
    });

    matches.sort((a, b) => {
        if (a.grouping !== b.grouping) {
            return (a.grouping ?? "").localeCompare(b.grouping ?? "", locale, {
                sensitivity: "accent",
            });
        }

        if (text) {
            const aMatch = a.name.toLowerCase().includes(text) ? 0 : 1;
            const bMatch = b.name.toLowerCase().includes(text) ? 0 : 1;
            if (aMatch !== bMatch) {
                return aMatch - bMatch;
            }
        }

        return a.name.localeCompare(b.name, locale, { sensitivity: "accent" });
    });

    return matches;
}

export const ELEMENT_IDS = Object.freeze({
    renderContainer: "render-container",
    mainCanvas: "main-canvas",
    markupContainer: "markup-container",
    markersContainer: "markers-container",
    mobileTopBar: "mobile-top-bar",
});

export async function createBookmarkImg(canvas: HTMLCanvasElement): Promise<string> {
    const width = canvas.width;
    const height = canvas.height;
    let dx = 0;
    let dy = 0;

    if (height / width < 0.7) {
        dx = width - Math.round((height * 10) / 7);
    } else {
        dy = height - Math.round(width * 0.7);
    }

    const dist = document.createElement("canvas");
    dist.height = 70;
    dist.width = 100;
    const ctx = dist.getContext("2d")!;

    ctx.drawImage(
        canvas,
        Math.round(dx / 2),
        Math.round(dy / 2),
        width - dx,
        height - dy,
        0,
        0,
        dist.width,
        dist.height,
    );

    return dist.toDataURL("image/jpeg");
}

type CreateSnapshotImgInnerOptions = {
    width?: number;
    height?: number;
    type?: "jpeg" | "png";
};

async function createSnapshotImgInner(options: CreateSnapshotImgInnerOptions = {}) {
    // captureNextFrame resolves with a 2D canvas copy of the WebGL frame
    const frame = await captureNextFrame();

    const markupEl = document.getElementById(ELEMENT_IDS.markupContainer);
    if (markupEl) {
        try {
            const svgStr = new XMLSerializer().serializeToString(markupEl);
            // base64 data URL avoids blob: URL restrictions in WKWebView
            const encoded = new TextEncoder().encode(svgStr);
            const binary = Array.from(encoded, (byte) => String.fromCodePoint(byte)).join("");
            const dataUrl = "data:image/svg+xml;base64," + btoa(binary);
            await new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                    frame.getContext("2d")!.drawImage(img, 0, 0, frame.width, frame.height);
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = dataUrl;
            });
        } catch {
            // skip overlay on serialization failure
        }
    }

    const { sx, sy, sw, sh, targetWidth, targetHeight } = positionSnapshot(
        frame.width,
        frame.height,
        options.width,
        options.height,
    );

    const dist = document.createElement("canvas");
    dist.width = targetWidth;
    dist.height = targetHeight;
    dist.getContext("2d")!.drawImage(frame, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
    return dist;
}

export async function createSnapshotImgAsArrayBuffer(
    options: CreateSnapshotImgInnerOptions = {},
): Promise<ArrayBuffer> {
    const dist = await createSnapshotImgInner(options);
    const blob = await new Promise<Blob | null>((resolve) => {
        dist.toBlob(resolve, `image/${options.type || "jpeg"}`);
    });
    if (!blob) {
        throw new Error("Failed to create snapshot blob");
    }
    return await blob.arrayBuffer();
}

export async function createSnapshotImg(options: CreateSnapshotImgInnerOptions = {}) {
    const dist = await createSnapshotImgInner(options);
    return dist.toDataURL(`image/${options.type || "jpeg"}`);
}

// exported for testing
export function positionSnapshot(
    width: number,
    height: number,
    requestedWidth: number | undefined,
    requestedHeight: number | undefined,
) {
    const aspectRatio = width / height;

    let targetWidth = 0;
    let targetHeight = 0;

    if (requestedWidth && requestedHeight) {
        targetWidth = requestedWidth;
        targetHeight = requestedHeight;
    } else if (requestedWidth) {
        targetWidth = requestedWidth;
        targetHeight = Math.round(requestedWidth / aspectRatio);
    } else if (requestedHeight) {
        targetWidth = Math.round(requestedHeight * aspectRatio);
        targetHeight = requestedHeight;
    } else {
        targetWidth = width;
        targetHeight = height;
    }

    let dx = 0;
    let dy = 0;

    if (targetWidth <= 0 || targetHeight <= 0) {
        throw new Error(`Invalid target dimensions: targetWidth=${targetWidth}, targetHeight=${targetHeight}`);
    }
    const targetAspectRatio = targetWidth / targetHeight;

    if (Math.abs(aspectRatio - targetAspectRatio) >= 0.01) {
        if (aspectRatio < targetAspectRatio) {
            dy = height - Math.round(height * (aspectRatio / targetAspectRatio));
        } else {
            dx = width - Math.round(width * (targetAspectRatio / aspectRatio));
        }
    }

    return {
        sx: Math.round(dx / 2),
        sy: Math.round(dy / 2),
        sw: width - dx,
        sh: height - dy,
        targetWidth,
        targetHeight,
    };
}
